"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowLeft, Loader2 } from "lucide-react";
import type React from "react";
import { useEffect, useRef } from "react";

interface CodeStepProps {
  codeValue: string;
  isVerifying: boolean;
  isSending: boolean;
  canResend: boolean;
  resendSeconds: number;
  onResend: () => void;
  hasError?: boolean;
  errorMsg?: string;
  infoMsg?: string;
  onCodeChange: (code: string) => void;
  onVerify: () => void;
  onGoBack: () => void;
}

function sanitizeCode(value: string): string {
  return value.replace(/[^0-9A-Za-z]/g, "").slice(0, 6);
}

export function CodeStep({
  codeValue,
  isVerifying,
  isSending,
  canResend,
  resendSeconds,
  onResend,
  hasError = false,
  errorMsg,
  infoMsg,
  onCodeChange,
  onVerify,
  onGoBack,
}: CodeStepProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const canVerify = codeValue.length === 6 && !isVerifying && !isSending;
  const chars = Array.from({ length: 6 }, (_, index) => codeValue[index] || "");

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const focusIndex = (index: number) => {
    const el = inputsRef.current[Math.max(0, Math.min(5, index))];
    el?.focus();
    el?.select();
  };

  const handleChange = (index: number, raw: string) => {
    const cleaned = sanitizeCode(raw);

    // Paste / autofill of multiple chars into one box.
    if (cleaned.length > 1) {
      const next = sanitizeCode(codeValue.slice(0, index) + cleaned);
      onCodeChange(next);
      focusIndex(Math.min(next.length, 5));
      return;
    }

    if (cleaned.length === 0) {
      const next = codeValue.slice(0, index) + codeValue.slice(index + 1);
      onCodeChange(next);
      return;
    }

    // Replace the char at this index (or append if typing at the end).
    const next = sanitizeCode(codeValue.slice(0, index) + cleaned + codeValue.slice(index + 1));
    onCodeChange(next);
    if (index < 5 && next.length > index) {
      focusIndex(index + 1);
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (codeValue[index]) {
        onCodeChange(codeValue.slice(0, index) + codeValue.slice(index + 1));
        focusIndex(index);
      } else if (index > 0) {
        onCodeChange(codeValue.slice(0, index - 1) + codeValue.slice(index));
        focusIndex(index - 1);
      }
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusIndex(index - 1);
      return;
    }

    if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      focusIndex(index + 1);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canVerify) onVerify();
      }}
      className="space-y-4"
    >
      <div className="mt-6">
        <p className="text-sm font-semibold text-foreground">Confirmation code</p>
        {infoMsg ? <p className="mt-2 text-xs text-muted-foreground">{infoMsg}</p> : null}
      </div>

      <div className="flex justify-center w-full">
        <div className="flex justify-between gap-2">
          {chars.map((char, index) => (
            <input
              key={index}
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              data-otp-slot="true"
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={6}
              value={char}
              disabled={isVerifying || isSending}
              aria-label={`Digit ${index + 1} of 6`}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onFocus={(event) => event.currentTarget.select()}
              className={cn(
                "w-12 h-12 aspect-square text-center text-base font-semibold border rounded-xl transition-colors outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50",
                hasError ? "border-destructive/70 bg-destructive/5 text-destructive" : "border-border bg-card text-foreground",
              )}
            />
          ))}
        </div>
      </div>

      {errorMsg ? <p className="text-xs text-destructive font-medium text-left">{errorMsg}</p> : null}

      <div className="grid grid-cols-1 gap-2 pt-2">
        <Button
          type="submit"
          disabled={!canVerify}
          className="w-full h-10 rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground transition-all cursor-pointer disabled:opacity-50"
        >
          {isVerifying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Verifying code</span>
            </>
          ) : (
            <span>Verify code</span>
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          disabled={isVerifying || isSending}
          className="w-full h-10 rounded-xl text-xs font-semibold text-muted-foreground hover:text-foreground transition-all cursor-pointer flex items-center justify-center gap-2"
          onClick={onGoBack}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Change email</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!canResend}
          onClick={onResend}
          className="w-full h-10 rounded-xl text-xs font-semibold cursor-pointer"
        >
          {isSending ? "Sending code" : resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : "Resend code"}
        </Button>
      </div>
    </form>
  );
}
